// ─────────────────────────────────────────────────────────────
// LoginScreen — shown before the user has a session.
// Manages its own sub-view: login | register | forgot | reset.
// On successful login or register, stores the token/user in
// localStorage and calls onLogin(user).
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  TextField,
  Typography,
  Alert,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MapsLogo } from "../../components/MapsLogo";
import { authApi } from "../../api/client";
import type { AuthUser } from "../../api/client";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

type SubView = "login" | "register" | "forgot" | "reset";

interface LoginScreenProps {
  onLogin: (user: AuthUser) => void;
  /** Error message passed in from the app shell after a failed SSO redirect. */
  ssoError?: string;
}

// ── Microsoft "M" logo ─────────────────────────────────────────
// Four coloured squares in the official 2×2 arrangement.
function MicrosoftLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 21 21"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function LoginScreen({ onLogin, ssoError }: LoginScreenProps) {
  const [subView, setSubView] = useState<SubView>("login");
  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoConfigError, setSsoConfigError] = useState<string | null>(null);
  const [dismissedSsoError, setDismissedSsoError] = useState(false);
  // Progressive disclosure: the login view shows ONLY the SSO hero by default.
  // The email/password form stays collapsed until the user explicitly opts in,
  // so there is nothing tempting to type into for TF SSO users (who usually
  // have no local password).
  const [showEmailForm, setShowEmailForm] = useState(false);
  // null  = probe in flight (don't render the button yet, no flicker)
  // true  = backend reported SSO is configured — render the button
  // false = backend returned 503 (or the probe failed) — hide the button entirely
  const [ssoAvailable, setSsoAvailable] = useState<boolean | null>(null);

  // ── Dev preview affordance ──────────────────────────────────────────────
  // In local dev there are no Azure env vars, so the backend's
  // /api/auth/microsoft/login probe returns 503 and the SSO hero would be
  // hidden — making the new SSO-first design impossible to review at
  // localhost. When running a dev build (or when VITE_FORCE_SSO_PREVIEW is
  // set) we force the button to render so Greg can SEE the redesign. Clicking
  // it locally still surfaces the real "SSO not available" config error via
  // handleMicrosoftSignIn — production gating is untouched because the real
  // probe result (`ssoAvailable === true`) is the only thing that gates it in
  // a production build. Paul can decide later whether this preview flag stays.
  const forceSsoPreview =
    import.meta.env.DEV ||
    import.meta.env.VITE_FORCE_SSO_PREVIEW === "true";
  const showSsoButton = ssoAvailable === true || forceSsoPreview;

  // Probe the SSO endpoint once on mount. The backend returns 503 when
  // Microsoft Entra credentials are not configured (e.g. local dev without
  // Azure env vars). In that case we hide the button so the user isn't
  // shown an option that can never work in this environment.
  //
  // Any non-2xx response — including network errors — is treated as
  // "unavailable". Showing a broken button is worse than hiding it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/microsoft/login`);
        if (cancelled) return;
        setSsoAvailable(res.ok);
      } catch {
        if (cancelled) return;
        setSsoAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLoginSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      localStorage.setItem("auth_token", res.access_token);
      localStorage.setItem("auth_user", JSON.stringify(res.user));
      onLogin(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegisterSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!fullName.trim()) {
      setFullNameError("Full name is required.");
      return;
    }
    setFullNameError(null);
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.register(email, password, fullName.trim());
      localStorage.setItem("auth_token", res.access_token);
      localStorage.setItem("auth_user", JSON.stringify(res.user));
      onLogin(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed.");
    } finally {
      setLoading(false);
    }
  }

  async function handleMicrosoftSignIn() {
    setSsoConfigError(null);
    setSsoLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/microsoft/login`);
      if (res.status === 503) {
        setSsoConfigError(
          "Microsoft SSO is not available — contact your administrator.",
        );
        return;
      }
      if (!res.ok) {
        setSsoConfigError(
          "Microsoft SSO is not available — contact your administrator.",
        );
        return;
      }
      const data = (await res.json()) as { redirect_url?: string };
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        // Navigation is in flight — keep the loading state up so the
        // button doesn't flicker back to its idle state before the page
        // leaves. setLoading(false) is intentionally omitted here.
        return;
      }
      setSsoConfigError(
        "Microsoft SSO is not available — contact your administrator.",
      );
    } catch {
      setSsoConfigError(
        "Microsoft SSO is not available — contact your administrator.",
      );
    } finally {
      setSsoLoading(false);
    }
  }

  function handleNav(view: SubView) {
    setError(null);
    setSubView(view);
  }

  // The SSO error passed in from the app shell (URL param) is only shown
  // on the login sub-view and can be dismissed independently of `error`.
  const showInboundSsoError =
    subView === "login" && !!ssoError && !dismissedSsoError;

  return (
    <Box
      sx={(theme) => ({
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
        backgroundImage: `
          repeating-linear-gradient(
            45deg,
            transparent 0,
            transparent 39px,
            ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.045)"} 39px,
            ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.045)"} 40px
          ),
          repeating-linear-gradient(
            -45deg,
            transparent 0,
            transparent 39px,
            ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.045)"} 39px,
            ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.045)" : "rgba(0,0,0,0.045)"} 40px
          )
        `,
      })}
    >
      <Card sx={{ width: "100%", maxWidth: 480 }}>
        <CardContent sx={{ p: 3 }}>
          {/* Logo — centered vertical stack: Maps pill, then title.
              The Maps pill is sized to ~130px via an explicit font-size
              override (independent of card width). MapsLogo renders text inside a <span>, so we scale
              it LOCALLY by overriding the inner span's font-size + padding
              via a wrapper sx (text scales crisply — no raster blur). The
              shared MapsLogo presets are NOT modified. */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              mb: 5,
              // Override the inner pill span only for this login-screen instance.
              "& > span": {
                fontSize: "2rem",
                px: 1.5,
                py: 0.5,
                borderRadius: "10px",
              },
            }}
          >
            <MapsLogo size="large" />
            <Typography variant="h3" sx={{ textAlign: "center", lineHeight: 1.2 }}>
              Discover Studio
            </Typography>
          </Box>

          {/* SSO error from a failed redirect — shown above everything on the login view */}
          {showInboundSsoError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setDismissedSsoError(true)}
            >
              {ssoError}
            </Alert>
          )}

          {/* General form errors */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Login view */}
          {subView === "login" && (
            <>
              {/* ── Microsoft SSO hero ──────────────────────────────────────
                  SSO is the fastest, no-password path for Thermo Fisher users,
                  so it is the visually dominant primary action: full-width,
                  generous padding, top of the form. In a production build it
                  only renders when the backend's /api/auth/microsoft/login
                  probe succeeded (ssoAvailable === true). In dev builds the
                  forceSsoPreview override (see above) renders it anyway so the
                  design can be reviewed at localhost. */}
              {showSsoButton && (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleMicrosoftSignIn}
                    disabled={ssoLoading || loading}
                    startIcon={
                      ssoLoading
                        ? <CircularProgress size={18} color="inherit" />
                        : <MicrosoftLogo />
                    }
                    sx={(theme) => {
                      const dark = theme.palette.mode === "dark";
                      // Microsoft's OFFICIAL sign-in button variants — brand
                      // compliant in both themes. Light: white bg / dark text.
                      // Dark: #2F2F2F bg / white text / light grey border.
                      const bg = dark ? "#2F2F2F" : "#FFFFFF";
                      const fg = dark ? "#FFFFFF" : "rgba(0,0,0,0.87)";
                      const border = dark ? "#8C8C8C" : "rgba(0,0,0,0.23)";
                      const hoverBg = dark ? "#3a3a3a" : "#f5f5f5";
                      const hoverBorder = dark ? "#a6a6a6" : "rgba(0,0,0,0.4)";
                      return {
                        py: 1.5,
                        bgcolor: bg,
                        color: fg,
                        border: "1px solid",
                        borderColor: border,
                        boxShadow: "none",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        letterSpacing: "0.01em",
                        "&:hover": {
                          bgcolor: hoverBg,
                          borderColor: hoverBorder,
                          boxShadow: "none",
                        },
                        "&:focus-visible": {
                          outline: "3px solid",
                          outlineColor: "primary.main",
                          outlineOffset: "2px",
                        },
                      };
                    }}
                  >
                    {ssoLoading ? (
                      "Redirecting…"
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          lineHeight: 1.2,
                        }}
                      >
                        <span>Sign in with your Thermo Fisher account</span>
                        <span style={{ fontSize: "0.78em", opacity: 0.65 }}>
                          Microsoft work or school account
                        </span>
                      </Box>
                    )}
                  </Button>

                  {/* Click-time SSO error (e.g. transient network failure when
                      requesting the redirect URL, or the dev-preview 503). */}
                  {ssoConfigError && (
                    <Alert
                      severity="error"
                      sx={{ mt: 1.5 }}
                      onClose={() => setSsoConfigError(null)}
                    >
                      {ssoConfigError}
                    </Alert>
                  )}
                </>
              )}

              {/* ── Email/password fallback (progressive disclosure) ────────
                  Collapsed by default so there's nothing tempting to type into
                  for SSO users. The toggle is a real button with aria-expanded
                  so screen readers announce the disclosure state. */}
              <Box sx={{ mt: showSsoButton ? 2 : 0 }}>
                <Button
                  onClick={() => setShowEmailForm((v) => !v)}
                  aria-expanded={showEmailForm}
                  aria-controls="email-login-form"
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transition: "transform 200ms cubic-bezier(0.2,0,0,1)",
                        transform: showEmailForm ? "rotate(180deg)" : "none",
                      }}
                    />
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 400,
                    color: "text.secondary",
                    px: 1,
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: "primary.main",
                      outlineOffset: "2px",
                    },
                  }}
                >
                  Sign in with email instead
                </Button>

                <Collapse in={showEmailForm} unmountOnExit>
                  <Box
                    id="email-login-form"
                    component="form"
                    onSubmit={handleLoginSubmit}
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}
                  >
                    <TextField
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      autoComplete="email"
                      disabled={loading}
                    />
                    <TextField
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      fullWidth
                      autoComplete="current-password"
                      disabled={loading}
                    />
                    {/* Demoted relative to the SSO hero — outlined, not filled,
                        so SSO still reads as the primary path. */}
                    <Button
                      type="submit"
                      variant="outlined"
                      fullWidth
                      disabled={loading}
                      sx={{ mt: 0.5 }}
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                      {loading ? "Signing in…" : "Sign In"}
                    </Button>
                    {/* Account links belong with the email path. */}
                    <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => handleNav("register")}
                        sx={{ textAlign: "center", cursor: "pointer" }}
                      >
                        Create an account
                      </Link>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => handleNav("forgot")}
                        sx={{ textAlign: "center", cursor: "pointer" }}
                      >
                        Forgot password?
                      </Link>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </>
          )}

          {/* Register view */}
          {subView === "register" && (
            <>
              <Typography variant="h3" gutterBottom>Create account</Typography>
              <Box component="form" onSubmit={handleRegisterSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  label="Full name"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); setFullNameError(null); }}
                  fullWidth
                  required
                  autoFocus
                  disabled={loading}
                  error={!!fullNameError}
                  helperText={fullNameError ?? undefined}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  autoComplete="email"
                  disabled={loading}
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  autoComplete="new-password"
                  disabled={loading}
                />
                <TextField
                  label="Confirm password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  autoComplete="new-password"
                  disabled={loading}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 0.5 }}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {loading ? "Creating account…" : "Create Account"}
                </Button>
              </Box>
              <Link
                component="button"
                variant="body2"
                onClick={() => handleNav("login")}
                sx={{ mt: 1.5, display: "block", cursor: "pointer" }}
              >
                Back to sign in
              </Link>
            </>
          )}

          {/* Forgot password view */}
          {subView === "forgot" && (
            <>
              <Typography variant="h3" gutterBottom>Forgot password</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Password resets are managed by your site administrator. Contact your admin for a reset link.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Administrator contact: <strong>maps-admin@thermofisher.com</strong>
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={() => handleNav("login")}
                sx={{ cursor: "pointer" }}
              >
                Back to sign in
              </Link>
            </>
          )}

          {/* Reset password view — accessed via URL param in a future sprint */}
          {subView === "reset" && (
            <>
              <Typography variant="h3" gutterBottom>Reset password</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField label="New password" type="password" fullWidth autoComplete="new-password" />
                <TextField label="Confirm new password" type="password" fullWidth autoComplete="new-password" />
                <Button variant="contained" fullWidth>Set New Password</Button>
              </Box>
              <Link
                component="button"
                variant="body2"
                onClick={() => handleNav("login")}
                sx={{ mt: 1.5, display: "block", cursor: "pointer" }}
              >
                Back to sign in
              </Link>
            </>
          )}

          {/* Card-level footer — shown beneath every sub-view. Quiet, muted,
              understated branding line separated from the content above. */}
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 4,
              lineHeight: 1.2,
            }}
          >
            Thermo Fisher Scientific
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
